import { DenialsPage, IDenialsPageProps, ProviderClaim } from '@enter/core';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DataTableCard, PageLayout, PageLayoutRow, ProviderMessagingContext } from 'components';
import { DialogContext } from 'services';
import { IObservedRootStore } from 'stores';
import providerWorkerStore from 'stores/worker.store';
import { ProviderCloseoutDialog } from '../claims/closeout-dialog/closeout-dialog';

const LAYOUT: IDenialsPageProps['layout'] = { Page: PageLayout, Row: PageLayoutRow, Card: DataTableCard };
const saveNothing = async (): Promise<void> => undefined;

/**
 * Denials & Rejections on the provider console: core's `DenialsPage`, which Support mounts too (FE-155), with
 * this console's own pieces — its dialogs, close-out, task compose, Workers store and claim routes.
 */
export const Denials: React.FC<IObservedRootStore> = inject('rootStore')(
    observer(({ rootStore }: IObservedRootStore) => {
        const { accountId } = useParams();
        const navigate = useNavigate();
        const dialogContext = React.useContext(DialogContext);
        const messagingContext = React.useContext(ProviderMessagingContext);
        const isDemo = Boolean(rootStore?.denialsStore.isDemo);

        // Stable, so the page re-renders on its own data rather than on this wrapper's.
        const consolePieces = React.useMemo(
            (): Omit<IDenialsPageProps, 'store' | 'workerStore' | 'layout'> => ({
                dialogs: {
                    showDialog: (Component, props): void => dialogContext.showDialog({ props, Component }),
                    hideDialog: dialogContext.hideDialog,
                },
                claimHref: claimId => (accountId ? `/${accountId}/claims/${claimId}` : `/claims/${claimId}`),
                onEditClaim: claimId =>
                    navigate(accountId ? `/${accountId}/claims/${claimId}/edit` : `/claims/${claimId}/edit`),
                openTaskCompose: options => messagingContext.openPanel?.({ ...options, isComposeOpen: true }),
                renderCloseout: (claim, done, cancel) => (
                    // The claim came from `providerDenialsApi`, so it is the provider console's own model.
                    <ProviderCloseoutDialog
                        claim={claim as ProviderClaim}
                        onCancel={cancel}
                        onSubmit={done}
                        // A demo claim is generated, so its close-out is kept by the page, never written (FE-187).
                        save={isDemo ? saveNothing : undefined}
                    />
                ),
                privatePayUnavailable:
                    'Not available here: converting a claim to private pay is a Support action, with no provider endpoint.',
            }),
            [accountId, navigate, dialogContext, messagingContext, isDemo],
        );

        if (!rootStore) {
            return <></>;
        }

        return (
            <DenialsPage
                store={rootStore.denialsStore}
                workerStore={providerWorkerStore}
                layout={LAYOUT}
                {...consolePieces}
            />
        );
    }),
);
